import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/soul";
import { BookOpen, Sparkles, Waves, Feather, ArrowRight } from "lucide-react";
import RevolvingBanner from "@/components/RevolvingBanner";
import StreakNudge from "@/components/StreakNudge";
import BreathGuide from "@/components/BreathGuide";
import MoodPulse from "@/components/MoodPulse";
import MirrorInvite from "@/components/MirrorInvite";

const HERO =
  "https://images.unsplash.com/photo-1490730141103-6cac27aaab94?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NTJ8MHwxfHNlYXJjaHwxfHx3YXJtJTIwbGlnaHQlMjBzcGlyaXR1YWwlMjByZWZsZWN0aW9ufGVufDB8fHx8MTc4NTM0ODk2Nnww&ixlib=rb-4.1.0&q=85";

const features = [
  {
    to: "/journal",
    icon: Feather,
    title: "Daily Truth Journal",
    body: "A quiet, unhurried place to lay down what you're carrying. Gentle prompts help honesty become a kind of freedom.",
    testid: "feature-journal",
  },
  {
    to: "/words",
    icon: BookOpen,
    title: "Word Power Explorer",
    body: "Trace the roots of the words you live by. Rediscover how logos — the spoken word — quietly shapes your world.",
    testid: "feature-words",
  },
  {
    to: "/mirror",
    icon: Waves,
    title: "Relationship Mirror",
    body: "Come to understand who you draw near, and why. Every person is a mirror, every reaction a tender teacher.",
    testid: "feature-mirror",
  },
  {
    to: "/red-letters",
    icon: Sparkles,
    title: "The Red Letters",
    body: "Sit a while with the words of Christ — the quiet map back to the kingdom of heaven already within you.",
    testid: "feature-red-letters",
  },
];

export default function Landing() {
  const [daily, setDaily] = useState(null);

  useEffect(() => {
    api.get("/red-letters/daily").then((r) => setDaily(r.data.verse)).catch(() => {});
  }, []);

  return (
    <main data-testid="landing-page">
      <MirrorInvite />
      <StreakNudge />
      <MoodPulse />
      {/* HERO */}
      <section className="relative min-h-[88vh] flex items-end overflow-hidden">
        <img
          src={HERO}
          alt="Reflection at dusk"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#110D14] via-[#110D14]/85 to-[#110D14]/30" />
        <div className="absolute inset-0 bg-[#110D14]/30" />

        <div className="relative z-10 max-w-7xl mx-auto px-6 pb-24 w-full">
          <div className="max-w-3xl fade-up">
            <p className="text-[#D4AF37] tracking-[0.3em] uppercase text-xs mb-6">
              A journey inward
            </p>
            <h1 className="serif text-5xl sm:text-6xl lg:text-7xl leading-[1.05] text-[#F5F1E7] font-light">
              Dive deep into
              <br />
              your own <span className="italic-serif text-[#D4AF37]">truth.</span>
            </h1>
            <p className="mt-8 text-lg text-[#A8A29E] max-w-xl leading-relaxed">
              The relationships we're drawn to, the words we speak, the ways we
              respond — all of it is a mirror, held up in love. Here we come home to the
              kingdom of heaven within, and to the truth that gently sets us free.
            </p>
            <div className="mt-10 flex flex-wrap gap-4">
              <Link
                to="/journal"
                data-testid="hero-start-journal"
                className="group inline-flex items-center gap-2 rounded-full bg-[#D4AF37] text-[#110D14] px-8 py-3.5 font-medium tracking-wide hover:bg-[#e6c860] transition-colors duration-300 gold-glow"
              >
                Begin your reflection
                <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform duration-300" />
              </Link>
              <Link
                to="/red-letters"
                data-testid="hero-red-letters"
                className="inline-flex items-center gap-2 rounded-full border border-white/15 px-8 py-3.5 text-[#F5F1E7] hover:border-[#D4AF37]/60 hover:text-[#D4AF37] transition-colors duration-300"
              >
                Read the Red Letters
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* DAILY RED LETTER */}
      {daily && (
        <section className="max-w-7xl mx-auto px-6 -mt-10 relative z-20">
          <div
            data-testid="daily-verse"
            className="glass rounded-2xl p-8 sm:p-12 border-l-2 border-l-[#8B0000]"
            style={{ borderLeftColor: "#C1272D" }}
          >
            <p className="text-xs uppercase tracking-[0.25em] text-[#A8A29E] mb-4">
              Today's Red Letter
            </p>
            <blockquote className="italic-serif text-3xl sm:text-4xl leading-snug text-[#C1272D]">
              “{daily.text}”
            </blockquote>
            <p className="mt-4 text-[#D4AF37] tracking-wide">— {daily.reference}</p>
            <p className="mt-4 text-[#A8A29E] max-w-2xl leading-relaxed">{daily.reflection}</p>
          </div>
        </section>
      )}

      {/* REVOLVING FEED */}
      <RevolvingBanner />

      {/* BREATH GUIDE */}
      <BreathGuide />

      {/* OUR HEART CALLOUT */}
      <section className="max-w-7xl mx-auto px-6 pb-8">
        <Link
          to="/our-heart"
          data-testid="landing-our-heart"
          className="group block glass rounded-3xl p-10 sm:p-14 hover:border-[#D4AF37]/40 transition-colors duration-300"
        >
          <p className="text-[#D4AF37] tracking-[0.3em] uppercase text-xs mb-4">Why we are here</p>
          <h2 className="serif text-3xl sm:text-5xl text-[#F5F1E7] font-light leading-tight max-w-3xl">
            Not a religion — a <span className="italic-serif text-[#D4AF37]">returning.</span>
          </h2>
          <p className="mt-5 text-[#B4A897] leading-relaxed max-w-2xl text-lg">
            We are all brothers and sisters, made by the same Light. There is no judgment here — only
            the quiet work of bringing the kingdom of heaven, already within us, down to earth.
          </p>
          <span className="mt-6 inline-flex items-center gap-2 text-[#D4AF37]">
            Read our heart
            <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform duration-300" />
          </span>
        </Link>
      </section>

      {/* FEATURES */}
      <section className="max-w-7xl mx-auto px-6 py-24">
        <div className="max-w-2xl mb-16">
          <h2 className="serif text-4xl sm:text-5xl text-[#F5F1E7] font-light">
            Four doorways <span className="italic-serif text-[#D4AF37]">inward</span>
          </h2>
          <p className="mt-4 text-[#A8A29E] leading-relaxed">
            Words carry power, and how we speak them matters. Little by little, telling the
            truth becomes the gentle way we come to hold everything within us.
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-6">
          {features.map((f, i) => {
            const Icon = f.icon;
            return (
              <Link
                key={f.to}
                to={f.to}
                data-testid={f.testid}
                className="group glass rounded-2xl p-8 sm:p-10 hover:border-[#D4AF37]/40 transition-colors duration-300"
                style={{ animationDelay: `${i * 0.08}s` }}
              >
                <div className="flex items-start justify-between">
                  <span className="h-12 w-12 rounded-xl bg-[#D4AF37]/10 grid place-items-center text-[#D4AF37] group-hover:bg-[#D4AF37]/20 transition-colors duration-300">
                    <Icon size={22} />
                  </span>
                  <ArrowRight
                    size={20}
                    className="text-[#A8A29E] group-hover:text-[#D4AF37] group-hover:translate-x-1 transition-all duration-300"
                  />
                </div>
                <h3 className="serif text-3xl mt-6 text-[#F5F1E7]">{f.title}</h3>
                <p className="mt-3 text-[#A8A29E] leading-relaxed">{f.body}</p>
              </Link>
            );
          })}
        </div>
      </section>
    </main>
  );
}
